import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CustomerNav from "@/components/layout/CustomerNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ChevronRight, FileUp, Loader2 } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from "recharts";
import { customerApi, formatApiError } from "@/lib/api";
import BodyCompReportView from "@/components/longevity/bodycomp/BodyCompReportView";
import HealthReportDialog from "@/components/longevity/HealthReportDialog";
import {
  TREND_METRICS, fmtValue, fmtTestTime, shortTestDate, reportValue,
} from "@/components/longevity/bodycomp/bodyCompFields";

const TrendChart = ({ metric, reports }) => {
  const data = useMemo(
    () =>
      [...reports]
        .reverse()
        .map((r) => ({ date: shortTestDate(r.test_time), value: reportValue(r, metric.key) }))
        .filter((p) => p.value !== null && p.value !== undefined),
    [reports, metric.key],
  );

  return (
    <Card className="rounded-2xl" data-testid={`body-comp-trend-${metric.key}`}>
      <CardContent className="p-5">
        <p className="tracking-overline text-[11px] text-muted-foreground mb-3">
          {metric.label.toUpperCase()}{metric.unit ? ` (${metric.unit})` : ""}
        </p>
        {data.length === 0 ? (
          <p className="text-xs text-muted-foreground py-8 text-center" data-testid={`body-comp-trend-empty-${metric.key}`}>
            No readings yet for this metric.
          </p>
        ) : (
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 18, right: 18, bottom: 4, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 10 }}
                  formatter={(v) => [`${fmtValue(v)}${metric.unit ? ` ${metric.unit}` : ""}`, metric.label]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={metric.color}
                  strokeWidth={2}
                  dot={{ r: 3, fill: metric.color }}
                  isAnimationActive={false}
                >
                  <LabelList dataKey="value" position="top" style={{ fontSize: 10, fill: "#555" }} formatter={(v) => fmtValue(v)} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Body Composition detail page — Latest / History / Trends.
 * Route: /portal/longevity/body-composition
 * Reads `GET /longevity/me/body-composition` (append-only store).
 */
export default function BodyCompositionDetail() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await customerApi.get("/longevity/me/body-composition");
      setReports(r.data?.reports || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const latest = reports[0] || null;
  const selected = reports.find((r) => r.id === selectedId) || null;

  return (
    <div>
      <CustomerNav />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <Button
              variant="ghost" size="sm" className="rounded-full -ml-2 mb-1 text-xs"
              onClick={() => navigate("/portal/longevity")}
              data-testid="body-comp-back-to-dashboard"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Longevity Overview
            </Button>
            <h1 className="font-editorial text-3xl sm:text-4xl leading-none" data-testid="body-comp-heading">
              Body Composition
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Your HOWBODY Body Composition Analysis Reports, newest first
            </p>
          </div>
          <Button className="rounded-full" onClick={() => setUploadOpen(true)} data-testid="body-comp-upload-btn">
            <FileUp className="w-4 h-4 mr-1.5" /> Upload Health Report
          </Button>
        </div>

        {loading ? (
          <Card className="rounded-2xl"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="body-comp-loading">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading your reports…
          </CardContent></Card>
        ) : (
          <Tabs defaultValue="latest" className="space-y-4" data-testid="body-comp-tabs">
            <TabsList data-testid="body-comp-tabs-list">
              <TabsTrigger value="latest" data-testid="body-comp-tab-latest">Latest</TabsTrigger>
              <TabsTrigger value="history" data-testid="body-comp-tab-history">History</TabsTrigger>
              <TabsTrigger value="trends" data-testid="body-comp-tab-trends">Trends</TabsTrigger>
            </TabsList>

            <TabsContent value="latest" data-testid="body-comp-panel-latest">
              <BodyCompReportView report={latest} />
            </TabsContent>

            <TabsContent value="history" className="space-y-4" data-testid="body-comp-panel-history">
              {reports.length === 0 ? (
                <Card className="rounded-2xl"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="body-comp-history-empty">
                  No reports yet. Upload your first Body Composition Analysis Report.
                </CardContent></Card>
              ) : (
                <Card className="rounded-2xl">
                  <CardContent className="p-2">
                    {reports.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-muted/60 transition-colors text-left"
                        data-testid={`body-comp-history-row-${r.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium">{fmtTestTime(r.test_time)}</p>
                          <p className="text-[11px] text-muted-foreground">
                            Comprehensive Score {fmtValue(reportValue(r, "comprehensive_score"), 0)} / 100
                          </p>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${r.id === selectedId ? "rotate-90" : ""}`} />
                      </button>
                    ))}
                  </CardContent>
                </Card>
              )}
              {selected && (
                <div data-testid="body-comp-history-detail">
                  <BodyCompReportView report={selected} />
                </div>
              )}
            </TabsContent>

            <TabsContent value="trends" data-testid="body-comp-panel-trends">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {TREND_METRICS.map((m) => (
                  <TrendChart key={m.key} metric={m} reports={reports} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>

      <HealthReportDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSavedBodyComp={load}
        onSavedSkin={load}
        onSaveVault={(payload) => customerApi.post("/longevity/me/biometric", payload)}
      />
    </div>
  );
}
